import { ChangeDetectorRef, Component, Input, QueryList, ViewChildren, AfterViewInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil, BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, startWith } from 'rxjs';
import { FormObjectListComponent } from '../../../shared/forms/form-object-list.component';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { ConcernCausePartComponent } from '../concern-cause-part/concern-cause-part.component';
import { ConcernCausePart } from '../models/concern-cause-part';
import { EstimateTotal } from '../models/estimates/estimate-total';
import { EstimateTotalService } from '../services/estimate-total.service';
import { UserCache } from '../services/user/user.cache';

@Component({
  selector: 'concern-cause-component-list',
  templateUrl: './concern-cause-component-list.component.html',
  styleUrls: ['./concern-cause-component-list.component.scss']
})
export class ConcernCauseComponentListComponent extends FormObjectListComponent<ConcernCausePartComponent, ConcernCausePart> implements AfterViewInit, OnDestroy {
  @Input() isReadOnly: boolean = false;
  @ViewChildren(ConcernCausePartComponent) override objectForms: QueryList<ConcernCausePartComponent>;

  private readonly _destroying$ = new Subject<void>();
  private confirmationDialog: MatDialogRef<DialogComponent>;

  // Reactive state for cached totals
  private readonly _forceRecalculation$ = new BehaviorSubject<number>(0);
  private _cachedLaborTotal: number = 0;
  private _cachedLaborTaxTotal: number = 0;
  private _cachedPartTotal: number = 0;
  private _cachedPartTaxTotal: number = 0;
  private _cachedSupplyTotal: number = 0;
  private _cachedSupplyTaxTotal: number = 0;
  private _cacheValid: boolean = false;

  constructor(override changeDetectorRef: ChangeDetectorRef,
              private dialog: MatDialog,
              private estimateTotalService: EstimateTotalService,
              private userCache: UserCache) {
    super(changeDetectorRef);
  }

  ngOnInit(): void {
    // Initialize with empty array first to prevent initial form control errors
    this.objectList = [];

    // Register global trigger for individual estimate components
    EstimateTotalService.setGlobalRecalculationTrigger(() => {
      this.triggerRecalculation();
    });

    // Add initial item after a brief delay to ensure proper initialization
    setTimeout(() => {
      this.objectList = [ new ConcernCausePart(null, null, null) ];
      this.changeDetectorRef.detectChanges();
      // Trigger initial calculation after components are initialized
      setTimeout(() => {
        this.triggerRecalculation();
      }, 100);
    }, 0);

    // Set up reactive updates only when data actually changes
    this._forceRecalculation$
      .pipe(
        takeUntil(this._destroying$),
        debounceTime(50), // Small debounce to prevent excessive calculations
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.recalculateAndUpdateService();
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  ngAfterViewInit(): void {
    // Monitor changes in ViewChildren to ensure proper initialization of new components
    this.objectForms.changes.subscribe(() => {
      setTimeout(() => {
        this.changeDetectorRef.detectChanges();
      }, 0);
    });
  }

  public override add(): void {
    super.add(new ConcernCausePart(null, null, null));
    // Force change detection to ensure new concern-cause-part component is properly initialized
    this.changeDetectorRef.detectChanges();

    // Invalidate cache since structure changed
    this.triggerRecalculation();

    // Additional timeout to ensure the new component is fully initialized
    setTimeout(() => {
      this.changeDetectorRef.detectChanges();
    }, 0);
  }

  public override remove(index: number): void {
    // Cannot remove first index.
    if (index > 0 && index < this.objectList.length) {
      // Clean up the concern before removing.
      this.objectForms.get(index)?.removing();

      super.remove(index);

      // Invalidate cache since structure changed
      this.triggerRecalculation();
    }
  }

  public confirmRemove(index: number): void {
    this.confirmationDialog = this.dialog.open(DialogComponent, {
      data: {
        title: 'Delete Customer Concern',
        bodyText: 'Are you sure you want to delete this Customer Concern?',
        cancelButtonText: 'Cancel',
        confirmationButtonText: 'Yes, Delete'
      }
    });

    this.confirmationDialog.afterClosed().subscribe(result => {
      if (result) {
        this.remove(index);
      }
    });
  }

  public override reset(): void {
    super.reset();
    this.objectList = [ new ConcernCausePart(null, null, null) ];
  }

  /**
   * Calculates the total estimate for all components in the list.
   * Tax percentages are ignore here and agregate at the estimate level for tax total.
   */
  public get estimateTotal(): EstimateTotal {
    console.count('ConcernCauseComponentListComponent.estimateTotal getter called');
    return new EstimateTotal(
        this.laborTotal,
        0, // laborTaxPercentage
        this.laborTaxTotal,
        this.partTotal,
        0, // partTaxPercentage
        this.partTaxTotal,
        this.supplyTotal,
        0, // supplyTaxPercentage
        this.supplyTaxTotal
    );
  }

  // Cached getters - only recalculate when cache is invalid
  public get laborTotal(): number {
    console.count('ConcernCauseComponentListComponent.laborTotal getter called');
    if (!this._cacheValid) {
      this.invalidateCacheAndRecalculate();
    }
    return this._cachedLaborTotal;
  }

  public get laborTaxTotal(): number {
    console.count('ConcernCauseComponentListComponent.laborTotal getter called');
    if (!this._cacheValid) {
      this.invalidateCacheAndRecalculate();
    }
    return this._cachedLaborTaxTotal;
  }

  public get partTotal(): number {
    console.count('ConcernCauseComponentListComponent.partTotal getter called');
    if (!this._cacheValid) {
      this.invalidateCacheAndRecalculate();
    }
    return this._cachedPartTotal;
  }

  public get partTaxTotal(): number {
    console.count('ConcernCauseComponentListComponent.partTaxTotal getter called')
    if (!this._cacheValid) {
      this.invalidateCacheAndRecalculate();
    }
    return this._cachedPartTaxTotal;
  }

  public get supplyTotal(): number {
    console.count('ConcernCauseComponentListComponent.supplyTotal getter called');
    if (!this._cacheValid) {
      this.invalidateCacheAndRecalculate();
    }
    return this._cachedSupplyTotal;
  }

  public get supplyTaxTotal(): number {
    console.count('ConcernCauseComponentListComponent.supplyTaxTotal getter called')
    if (!this._cacheValid) {
      this.invalidateCacheAndRecalculate();
    }
    return this._cachedSupplyTaxTotal;
  }

  public get laborTaxPercentage(): number {
    console.count('ConcernCauseComponentListComponent.laborTaxPercentage getter called')
    const rate = this.userCache?.selectedRepairFacility?.laborTaxRate ?? 0;
    return rate / 100; // Convert percentage to decimal for calculations
  }

  public get partTaxPercentage(): number {
    console.count('ConcernCauseComponentListComponent.partTaxPercentage getter called')
    const rate = this.userCache?.selectedRepairFacility?.partTaxRate ?? 0;
    return rate / 100; // Convert percentage to decimal for calculations
  }

  public get supplyTaxPercentage(): number {
    console.count('ConcernCauseComponentListComponent.supplyTaxPercentage getter called')
    const rate = this.userCache?.selectedRepairFacility?.partTaxRate ?? 0; // Tax is same as parts
    return rate / 100; // Convert percentage to decimal for calculations
  }

  /**
   * Forces cache invalidation and triggers recalculation (sync)
   */
  private invalidateCacheAndRecalculate(): void {
    this._cacheValid = false;
    this.recalculateAllTotals();
  }

  /**
   * Performs the expensive calculation only when needed
   */
  private recalculateAllTotals(): void {
    // console.log('Recalculating totals - expensive operation');

    this._cachedLaborTotal = 0;
    this._cachedLaborTaxTotal = 0;
    this._cachedPartTotal = 0;
    this._cachedPartTaxTotal = 0;
    this._cachedSupplyTotal = 0;
    this._cachedSupplyTaxTotal = 0;

    this.objectForms?.forEach(concern =>
      concern.correctionEstimateList?.objectForms.forEach(correction => {
        correction.estimateList.laborForms?.forEach(estimate => {
          this._cachedLaborTotal += estimate.total;
          this._cachedLaborTaxTotal += estimate.totalTax;
        });
        correction.estimateList.partForms?.forEach(estimate => {
          this._cachedPartTotal += estimate.total;
          this._cachedPartTaxTotal += estimate.totalTax;
        });
        correction.estimateList.materialForms?.forEach(estimate => {
          this._cachedSupplyTotal += estimate.total;
          this._cachedSupplyTaxTotal += estimate.totalTax;
        });
      })
    );

    this._cacheValid = true;
  }

  /**
   * Reactive recalculation triggered by observable
   */
  private recalculateAndUpdateService(): void {
    this.recalculateAllTotals();
    this.updateEstimateTotalService();
  }

  /**
   * Updates the EstimateTotalService with cached totals
   */
  private updateEstimateTotalService(): void {
    this.estimateTotalService.updateTotals(
      this._cachedLaborTotal,
      this.laborTaxPercentage,
      this._cachedLaborTaxTotal,
      this._cachedPartTotal,
      this.partTaxPercentage,
      this._cachedPartTaxTotal,
      this._cachedSupplyTotal,
      this.supplyTaxPercentage,
      this._cachedSupplyTaxTotal
    );
  }

  /**
   * Triggers recalculation when data changes
   */
  public triggerRecalculation(): void {
    this._cacheValid = false;
    this._forceRecalculation$.next(Date.now());
  }

  /**
   * Get the concern form control for a specific concern at the given index
   */
  public getConcernFormControl(index: number): FormControl | null {
    try {
      const concernComponent = this.objectForms?.get(index);
      if (!concernComponent?.form) {
        return null;
      }
      const control = concernComponent.form.get('concern') as FormControl;
      return control || null;
    } catch (error) {
      console.warn(`Error getting concern form control at index ${index}:`, error);
      return null;
    }
  }

  /**
   * Clear the concern field for a specific concern at the given index
   */
  public clearConcern(index: number): void {
    const formControl = this.getConcernFormControl(index);
    if (formControl) {
      formControl.setValue('');
    }
  }

  /**
   * Set the concern field value for a specific concern at the given index
   */
  public setConcernValue(index: number, value: string): void {
    const formControl = this.getConcernFormControl(index);
    if (formControl) {
      formControl.setValue(value);
    }
  }

  /**
   * TrackBy function for ngFor to improve performance and reduce expression changed errors
   * Using the item object reference instead of index to avoid issues with item removal
   */
  public trackByIndex(index: number, item: any): any {
    return item;
  }
}
